import React from 'react';
import { Bot, User, Star, MessageCircle, Clock } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import type { LangfuseTrace } from '@/types/traces';

interface ConversationViewProps {
  trace: LangfuseTrace;
}

interface ConversationMessage {
  role: 'user' | 'assistant' | 'system';
  content: string;
  timestamp?: string;
}

const formatDuration = (latency: number): string => {
  return `${latency.toFixed(2)}s`;
};

const formatScoreValue = (value: number): string => {
  if (typeof value === 'number') {
    return (value * 100).toFixed(1) + '%';
  }
  return String(value);
};

export const ConversationView: React.FC<ConversationViewProps> = ({ trace }) => {
  const messages: ConversationMessage[] = React.useMemo(() => {
    if (!trace.input || !Array.isArray(trace.input)) {
      return [];
    }
    
    const inputMessages = trace.input.map((msg: any) => {
      let content: string;
      
      if (typeof msg === 'string') {
        content = msg;
      } else if (msg.content) {
        // Handle different content formats
        if (typeof msg.content === 'string') {
          content = msg.content;
        } else if (msg.content.text) {
          // Handle {type: "text", text: "content"} format
          content = msg.content.text;
        } else if (Array.isArray(msg.content)) {
          // Handle array of content parts
          content = msg.content.map((part: any) => {
            if (typeof part === 'string') return part;
            if (part.text) return part.text;
            if (part.type === 'text' && part.text) return part.text;
            return JSON.stringify(part);
          }).join(' ');
        } else {
          content = JSON.stringify(msg.content);
        }
      } else if (msg.text) {
        content = msg.text;
      } else {
        content = JSON.stringify(msg);
      }
      
      return {
        role: msg.role || 'user',
        content,
        timestamp: msg.timestamp
      };
    });
    
    if (trace.output) {
      let outputContent: string;
      
      if (typeof trace.output === 'string') {
        outputContent = trace.output;
      } else if (trace.output.text) {
        outputContent = trace.output.text;
      } else if (trace.output.content) {
        if (typeof trace.output.content === 'string') {
          outputContent = trace.output.content;
        } else if (trace.output.content.text) {
          outputContent = trace.output.content.text;
        } else {
          outputContent = JSON.stringify(trace.output.content);
        }
      } else {
        outputContent = JSON.stringify(trace.output);
      }
      
      inputMessages.push({
        role: 'assistant',
        content: outputContent,
        timestamp: undefined
      });
    }
    
    return inputMessages;
  }, [trace.input, trace.output]);

  const assistantName = trace.metadata?.assistant_name || 'Asistente';
  const scores = trace.scores || [];

  const getMessageIcon = (role: string) => {
    switch (role) {
      case 'user':
        return <User className="h-4 w-4" />;
      case 'assistant':
        return <Bot className="h-4 w-4" />;
      case 'system':
        return <MessageCircle className="h-4 w-4" />;
      default:
        return <MessageCircle className="h-4 w-4" />;
    }
  };

  const getRoleLabel = (role: string) => {
    switch (role) {
      case 'user':
        return 'Usuario';
      case 'assistant':
        return assistantName;
      case 'system':
        return 'Sistema';
      default:
        return role;
    }
  };

  return (
    <div className="space-y-6">
      {/* Assistant Info */}
      <Card>
        <CardHeader className="pb-3">
          <div className="flex items-center gap-2">
            <Bot className="h-5 w-5 text-blue-600" />
            <div>
              <CardTitle className="text-lg">{assistantName}</CardTitle>
              <CardDescription className="flex items-center gap-4">
                <span className="flex items-center gap-1">
                  <Clock className="h-3 w-3" />
                  Duración: {trace.latency ? formatDuration(trace.latency) : 'N/A'}
                </span>
                <span>Sesión: {trace.sessionId?.slice(0, 8) || 'N/A'}</span>
              </CardDescription>
            </div>
          </div>
        </CardHeader>
      </Card>

      {/* Conversation Messages */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg">Conversación</CardTitle>
          <CardDescription>
            Intercambio de mensajes durante la llamada
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {messages.length === 0 ? (
            <div className="text-center py-4 text-gray-500">
              No se encontraron mensajes en esta conversación
            </div>
          ) : (
            messages
              .filter(message => message.role !== 'system') // Ocultar system prompts
              .map((message, index) => (
              <div
                key={index}
                className={`flex gap-3 p-4 rounded-lg border ${
                  message.role === 'user' 
                    ? 'bg-blue-50 border-blue-200' 
                    : message.role === 'assistant'
                    ? 'bg-gray-50 border-gray-200'
                    : 'bg-yellow-50 border-yellow-200'
                }`}
              >
                <div className="flex-shrink-0">
                  <div className={`p-2 rounded-full ${
                    message.role === 'user'
                      ? 'bg-blue-100 text-blue-600'
                      : message.role === 'assistant'
                      ? 'bg-gray-100 text-gray-600'
                      : 'bg-yellow-100 text-yellow-600'
                  }`}>
                    {getMessageIcon(message.role)}
                  </div>
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-2">
                    <span className="font-medium text-sm">
                      {getRoleLabel(message.role)}
                    </span>
                    {message.timestamp && (
                      <span className="text-xs text-gray-500">
                        {new Date(message.timestamp).toLocaleTimeString()}
                      </span>
                    )}
                  </div>
                  <div className="text-sm text-gray-700 whitespace-pre-wrap break-words">
                    {message.content}
                  </div>
                </div>
              </div>
            ))
          )}
        </CardContent>
      </Card>

      {/* Scores Section */}
      {scores.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <Star className="h-5 w-5 text-yellow-500" />
              Evaluaciones de Calidad
            </CardTitle>
            <CardDescription>
              Puntuaciones automáticas de la conversación
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              {scores.map((score: any, index: number) => (
                <div key={index} className="border rounded-lg p-4">
                  <div className="flex items-center justify-between mb-2">
                    <h4 className="font-medium">{score.name}</h4>
                    <Badge variant={score.value >= 0.7 ? 'default' : score.value >= 0.4 ? 'secondary' : 'destructive'}>
                      {formatScoreValue(score.value)}
                    </Badge>
                  </div>
                  {score.comment && (
                    <p className="text-sm text-gray-600 mt-2">
                      {score.comment}
                    </p>
                  )}
                  <div className="flex items-center gap-4 mt-2 text-xs text-gray-500">
                    <span>Fuente: {score.source}</span>
                    <span>Fecha: {new Date(score.timestamp).toLocaleString('es-ES')}</span>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

    </div>
  );
};